import { lazy, Suspense } from 'react';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { Spin } from 'antd';
import { AdminShell } from '@/app/shells/AdminShell';
import { EmployeeShell } from '@/app/shells/EmployeeShell';
import { ManagerShell } from '@/app/shells/ManagerShell';
import { RequireAuth } from '@/app/RequireAuth';
import { RequireShell, ShellRedirect } from '@/app/RequireShell';
import { RequireFeature } from '@/app/RequireFeature';

// Three shells, one app (02 §6): Employee (ESS), Manager (MSS), Admin, each
// route-guarded by role. Route-level code splitting keeps the initial bundle
// small — an employee never downloads the org chart editor.
const LoginPage = lazy(() => import('@/features/auth/LoginPage'));
const MeetConnectDone = lazy(() => import('@/features/chat/MeetConnectDone'));
const MfaEnrolmentPage = lazy(() => import('@/features/auth/MfaEnrolmentPage'));
const DashboardPage = lazy(() => import('@/features/dashboard/DashboardPage'));
const EmployeeListPage = lazy(() => import('@/features/employee/EmployeeListPage'));
const OrgPage = lazy(() => import('@/features/org/OrgPage'));
const OrgChartPage = lazy(() => import('@/features/org/OrgChartPage'));
const EmployeeProfilePage = lazy(() => import('@/features/employee/EmployeeProfilePage'));
const ConsentPage = lazy(() => import('@/features/consent/ConsentPage'));
const AuditPage = lazy(() => import('@/features/audit/AuditPage'));
const AttendancePage = lazy(() => import('@/features/attendance/AttendancePage'));
const LeavePage = lazy(() => import('@/features/leave/LeavePage'));
const MonthlyClosePage = lazy(() => import('@/features/attendance/MonthlyClosePage'));
const EssHome = lazy(() => import('@/features/dashboard/EssHome'));
const MssHome = lazy(() => import('@/features/dashboard/MssHome'));
const ApprovalsInbox = lazy(() => import('@/features/approvals/ApprovalsInbox'));
const MyPrivacyPage = lazy(() => import('@/features/privacy/MyPrivacyPage'));
const ReportsPage = lazy(() => import("@/features/reports/ReportsPage"));
const CyclesPage = lazy(() => import("@/features/performance/CyclesPage"));
const TeamReviewsPage = lazy(() => import("@/features/performance/TeamReviewsPage"));
const MyReviewPage = lazy(() => import("@/features/performance/MyReviewPage"));
const GoalsPage = lazy(() => import("@/features/performance/GoalsPage"));
const ImprovementPlansPage = lazy(() => import("@/features/performance/ImprovementPlansPage"));
const FeedbackRequestsPage = lazy(() => import("@/features/performance/FeedbackRequestsPage"));
const MyProfilePage = lazy(() => import('@/features/employee/MyProfilePage'));
const MyResignationPage = lazy(() => import('@/features/exit/MyResignationPage'));
const TeamPage = lazy(() => import('@/features/team/TeamPage'));
const NotificationsPage = lazy(() => import('@/features/notifications/NotificationsPage'));
const SetPasswordPage = lazy(() => import('@/features/auth/SetPasswordPage'));
const OnboardingPage = lazy(() => import('@/features/onboarding/OnboardingPage'));
const MyJoiningPage = lazy(() => import('@/features/onboarding/MyJoiningPage'));
const LettersPage = lazy(() => import('@/features/letters/LettersPage'));
const SalaryPage = lazy(() => import('@/features/payroll/SalaryPage'));
const PayrollRunsPage = lazy(() => import('@/features/payroll/PayrollRunsPage'));
const MyTaxDeclarationPage = lazy(() => import('@/features/payroll/MyTaxDeclarationPage'));
const MyPayPage = lazy(() => import('@/features/payroll/MyPayPage'));
const MyClaimsPage = lazy(() => import('@/features/reimbursements/MyClaimsPage'));
const ClaimsQueuePage = lazy(() => import('@/features/reimbursements/ClaimsQueuePage'));
const LoansPage = lazy(() => import('@/features/loans/LoansPage'));
const BulkActionsPage = lazy(() => import('@/features/bulk/BulkActionsPage'));
const LeaveConfigPage = lazy(() => import('@/features/leave/LeaveConfigPage'));
const AccessPage = lazy(() => import('@/features/access/AccessPage'));
const BankChangeQueuePage = lazy(() => import('@/features/employee/BankChangeQueuePage'));
const MyBankPage = lazy(() => import('@/features/employee/MyBankPage'));
const ExitsPage = lazy(() => import('@/features/exit/ExitsPage'));
const AnnouncementsAdminPage = lazy(() => import('@/features/dashboard/AnnouncementsAdminPage'));
const TaxDeclarationsPage = lazy(() => import('@/features/payroll/TaxDeclarationsPage'));
const IntegrityPage = lazy(() => import('@/features/attendance/IntegrityPage'));
const MyLettersPage = lazy(() => import('@/features/letters/MyLettersPage'));
const GetHelpPage = lazy(() => import('@/features/support/GetHelpPage'));
// Collaboration (PRD §5.14): only reachable when the workspace has it on.
const MessagesPage = lazy(() => import('@/features/chat/MessagesPage'));
const TasksPage = lazy(() => import('@/features/tasks/TasksPage'));

function collaborationRoutes(home: string) {
  return [
    <Route
      key="messages"
      path="messages/:channelId?"
      element={
        <RequireFeature feature="collaboration" fallback={home}>
          <MessagesPage />
        </RequireFeature>
      }
    />,
    <Route
      key="tasks"
      path="tasks"
      element={
        <RequireFeature feature="collaboration" fallback={home}>
          <TasksPage />
        </RequireFeature>
      }
    />,
  ];
}

function PageFallback() {
  return (
    <div style={{ display: 'grid', placeItems: 'center', minHeight: '60vh' }}>
      <Spin size="large" />
    </div>
  );
}

export function AppRouter() {
  return (
    <BrowserRouter>
      <Suspense fallback={<PageFallback />}>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          {/* Unauthenticated by necessity: a user who must enrol cannot sign in
              until they have, so they hold an enrolment token rather than a
              session. */}
          <Route path="/mfa/enrol" element={<MfaEnrolmentPage />} />
          {/* Setting a first password from an invitation. Unauthenticated for the
              same reason as enrolment: this person cannot sign in yet, and
              their credential is the single-use token in the link. */}
          <Route path="/set-password" element={<SetPasswordPage />} />
          {/* Where the Google Meet connection popup lands (FR-CHAT-11). It only
              tells the window that opened it how the connection went, so it
              needs no session of its own. */}
          <Route path="/oauth/google-meet/done" element={<MeetConnectDone />} />

          {/* Administration */}
          <Route
            path="/admin"
            element={
              <RequireAuth>
                <RequireShell shell="admin">
                  <AdminShell />
                </RequireShell>
              </RequireAuth>
            }
          >
            <Route index element={<DashboardPage />} />
            <Route path="employees" element={<EmployeeListPage />} />
            <Route path="employees/:id" element={<EmployeeProfilePage />} />
            <Route path="organisation" element={<OrgPage />} />
            <Route path="org-chart" element={<OrgChartPage />} />
            <Route path="attendance" element={<AttendancePage />} />
            <Route path="attendance/close" element={<MonthlyClosePage />} />
            <Route path="leave" element={<LeavePage />} />
            <Route path="onboarding" element={<OnboardingPage />} />
            <Route path="letters" element={<LettersPage />} />
            <Route path="salary" element={<SalaryPage />} />
            <Route path="payroll" element={<PayrollRunsPage />} />
            <Route path="claims" element={<ClaimsQueuePage />} />
            <Route path="loans" element={<LoansPage />} />
            <Route path="bulk" element={<BulkActionsPage />} />
            <Route path="leave-config" element={<LeaveConfigPage />} />
            <Route path="access" element={<AccessPage />} />
            <Route path="bank-changes" element={<BankChangeQueuePage />} />
            <Route path="exits" element={<ExitsPage />} />
            <Route path="announcements" element={<AnnouncementsAdminPage />} />
            <Route path="tax-declarations" element={<TaxDeclarationsPage />} />
            <Route path="attendance/integrity" element={<IntegrityPage />} />
            <Route path="privacy" element={<ConsentPage />} />
            <Route path="notifications" element={<NotificationsPage />} />
            <Route path="performance" element={<CyclesPage />} />
            <Route path="goals" element={<GoalsPage />} />
            <Route path="improvement-plans" element={<ImprovementPlansPage />} />
            <Route path="reports" element={<ReportsPage />} />
            <Route path="audit" element={<AuditPage />} />
            {collaborationRoutes('/admin')}
            {/* Asking for help is not a privileged action, so it appears in
                all three shells rather than behind an administrator. */}
            <Route path="help" element={<GetHelpPage />} />
          </Route>

          {/* Manager self-service */}
          <Route
            path="/mss"
            element={
              <RequireAuth>
                <RequireShell shell="mss">
                  <ManagerShell />
                </RequireShell>
              </RequireAuth>
            }
          >
            <Route index element={<MssHome />} />
            <Route path="approvals" element={<ApprovalsInbox />} />
            {collaborationRoutes('/mss')}
            <Route path="team" element={<TeamPage />} />
            <Route path="org-chart" element={<OrgChartPage />} />
            <Route path="performance" element={<TeamReviewsPage />} />
            <Route path="goals" element={<GoalsPage />} />
            <Route path="improvement-plans" element={<ImprovementPlansPage />} />
            <Route path="feedback-requests" element={<FeedbackRequestsPage />} />
            {/* A manager decides expense claims from their own reportees; the
                server scopes the queue to their reporting tree. */}
            <Route path="claims" element={<ClaimsQueuePage />} />
            {/* A manager is also an employee, and marks their own attendance
                without switching shells to do it. */}
            <Route path="attendance" element={<AttendancePage />} />
            <Route path="notifications" element={<NotificationsPage />} />
            <Route path="help" element={<GetHelpPage />} />
          </Route>

          {/* Employee self-service */}
          <Route
            path="/ess"
            element={
              <RequireAuth>
                <RequireShell shell="ess">
                  <EmployeeShell />
                </RequireShell>
              </RequireAuth>
            }
          >
            <Route index element={<EssHome />} />
            <Route path="attendance" element={<AttendancePage />} />
            <Route path="leave" element={<LeavePage />} />
            <Route path="profile" element={<MyProfilePage />} />
            <Route path="org-chart" element={<OrgChartPage />} />
            <Route path="resignation" element={<MyResignationPage />} />
            <Route path="joining" element={<MyJoiningPage />} />
            <Route path="letters" element={<MyLettersPage />} />
            <Route path="pay" element={<MyPayPage />} />
            <Route path="claims" element={<MyClaimsPage />} />
            <Route path="bank" element={<MyBankPage />} />
            <Route path="tax" element={<MyTaxDeclarationPage />} />
            <Route path="performance" element={<MyReviewPage />} />
            <Route path="goals" element={<GoalsPage />} />
            <Route path="improvement-plans" element={<ImprovementPlansPage />} />
            <Route path="feedback-requests" element={<FeedbackRequestsPage />} />
            <Route path="privacy" element={<MyPrivacyPage />} />
            {collaborationRoutes('/ess')}
            <Route path="notifications" element={<NotificationsPage />} />
            <Route path="help" element={<GetHelpPage />} />
          </Route>

          {/* Landing is decided by role rather than fixed to /admin, so an
              employee signing in does not bounce off a 403 on the way to their
              own home page. */}
          <Route
            path="*"
            element={
              <RequireAuth>
                <ShellRedirect />
              </RequireAuth>
            }
          />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}
